import React, { useState, useEffect, useCallback } from 'react';
import { fetchUserGiftCards, getGiftCardStatus } from '../utils/giftCardHelpers';
import { formatTimestamp } from '../utils/membershipHelpers';
import GiftCardTransfer from './GiftCardTransfer';
import '../styles/giftcards.css';

const GiftCards = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('mycards');
  const [giftCards, setGiftCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [transferCard, setTransferCard] = useState(null);

  const loadGiftCards = useCallback(async () => {
    setLoadingCards(true);
    try {
      const cards = await fetchUserGiftCards(userId);
      setGiftCards(cards);
    } catch (err) {
      console.error('Error loading gift cards:', err);
    } finally {
      setLoadingCards(false);
    }
  }, [userId]);

  useEffect(() => {
    if (activeTab === 'mycards') {
      loadGiftCards();
    }
  }, [activeTab, loadGiftCards]);

  const handleTransferComplete = () => {
    setTransferCard(null);
    loadGiftCards();
  };

  const renderPurchaseTab = () => (
    <div className="giftcard-purchase">
      <div className="giftcard-coming-soon">
        <div className="coming-soon-icon">🎁</div>
        <h3>Coming Soon</h3>
        <p>Gift card purchase will be available soon. Check back later!</p>
      </div>
    </div>
  );

  const renderMyCardsTab = () => (
    <div className="giftcard-list">
      {loadingCards ? (
        <div className="giftcard-loading">Loading gift cards...</div>
      ) : giftCards.length === 0 ? (
        <div className="giftcard-empty">
          <div className="empty-icon">🎁</div>
          <h3>No Gift Cards</h3>
          <p>You don't have any gift cards yet.</p>
        </div>
      ) : (
        giftCards.map((card) => {
          const status = getGiftCardStatus(card);
          return (
            <div key={card.id} className="giftcard-item">
              <div className="giftcard-item-header">
                <div className="giftcard-item-info">
                  <div>
                    <span className="giftcard-coins">{card.coinAmount} tokens ({card.guaranteedPoints} points guaranteed)</span>
                    {card.code && (
                      <span className="giftcard-code">Code: {card.code}</span>
                    )}
                  </div>
                </div>
                <span className={`giftcard-status ${status.toLowerCase()}`}>{status}</span>
              </div>
              <div className="giftcard-item-details">
                {card.activatedAt && (
                  <div className="giftcard-date">Activated: {formatTimestamp(card.activatedAt)}</div>
                )}
                {status === 'Redeemed' && card.redeemedAt && (
                  <div className="giftcard-date">Redeemed: {formatTimestamp(card.redeemedAt)}</div>
                )}
              </div>
              {status === 'Active' && (
                <button
                  className="giftcard-transfer-btn"
                  onClick={() => setTransferCard(card)}
                >
                  Transfer
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="giftcards-section">
      <div className="giftcards-container">
        <div className="giftcards-tabs">
          <div
            className={`giftcards-tab ${activeTab === 'mycards' ? 'active' : ''}`}
            onClick={() => setActiveTab('mycards')}
          >
            <h3>My Gift Cards</h3>
          </div>
          <div
            className={`giftcards-tab ${activeTab === 'purchase' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchase')}
          >
            <h3>Purchase</h3>
          </div>
        </div>

        <div className="giftcards-content">
          {activeTab === 'mycards' && renderMyCardsTab()}
          {activeTab === 'purchase' && renderPurchaseTab()}
        </div>
      </div>

      {transferCard && (
        <GiftCardTransfer
          giftCard={transferCard}
          currentUserId={userId}
          onTransferComplete={handleTransferComplete}
          onCancel={() => setTransferCard(null)}
        />
      )}
    </div>
  );
};

export default GiftCards;
